import { useEffect, useRef } from "react";

const TYPING_TAGS = ["INPUT", "TEXTAREA", "SELECT"];

/**
 * A keystroke aimed at a field is the user editing text, not firing a shortcut —
 * Backspace in a keyword search must delete a character, not rows.
 */
const isTypingTarget = (target) =>
  !!target &&
  (target.isContentEditable || TYPING_TAGS.includes(target.tagName));

/**
 * Document-level keydown shortcut.
 *
 * @param keys     KeyboardEvent.key value, or a list of them (e.g. ["Delete", "Backspace"])
 * @param handler  receives the event. Re-read on every keypress, so it can close
 *                 over fresh state without re-binding the listener.
 * @param options  enabled: bind only while true — the usual way to scope a
 *                 shortcut to a state (e.g. "some rows are selected").
 *                 ignoreWhenTyping: skip events aimed at inputs / contentEditable.
 */
export const useKeyDown = (
  keys,
  handler,
  { enabled = true, ignoreWhenTyping = true } = {}
) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  // joined so an inline array literal doesn't re-bind the listener every render
  const keysKey = (Array.isArray(keys) ? keys : [keys]).join(",");

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      if (!keysKey.split(",").includes(e.key)) return;
      if (ignoreWhenTyping && isTypingTarget(e.target)) return;
      handlerRef.current(e);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, keysKey, ignoreWhenTyping]);
};
